import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ChevronDown, MessageCircleHeart } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import { IMAGES, TREATMENTS, HOSPITALS } from "../content/siteContent";
import { SakuraBranch, FloatingPetals, VerticalKanji } from "./JapaneseMotifs";

function MaskedLine({ children, delay }) {
  return (
    <span className="mask-line">
      <motion.span
        className="block"
        initial={{ y: "110%" }}
        animate={{ y: 0 }}
        transition={{ duration: 0.9, delay, ease: [0.22, 1, 0.36, 1] }}
      >
        {children}
      </motion.span>
    </span>
  );
}

export function Hero() {
  const { t } = useLang();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const baymaxY = useTransform(scrollYProgress, [0, 1], [0, 90]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  const openChat = () => window.dispatchEvent(new CustomEvent("open-tudi-chat"));

  return (
    <section ref={ref} className="relative min-h-[100svh] flex items-center overflow-hidden bg-ice">
      <FloatingPetals />
      <SakuraBranch className="absolute top-0 right-0 w-[240px] sm:w-[320px] opacity-40 pointer-events-none z-[1]" />
      <VerticalKanji text="先端医療" className="hidden xl:block absolute right-5 top-1/3 text-xs tracking-[0.4em] text-ink/25 z-[1]" />
      <div className="absolute -top-32 -right-32 w-[560px] h-[560px] rounded-full bg-aqua/40 blur-3xl" />
      <div className="absolute top-1/3 -left-40 w-[420px] h-[420px] rounded-full bg-blush blur-3xl" />
      <div className="absolute bottom-0 right-1/4 w-[380px] h-[380px] rounded-full bg-aqua/25 blur-3xl" />

      <div className="relative z-10 max-w-7xl mx-auto px-6 pt-32 pb-20 w-full grid lg:grid-cols-2 gap-12 items-center">
        <motion.div style={{ opacity: fade }}>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.7 }}
            className="inline-flex items-center gap-2 rounded-full border border-brand/25 bg-white/80 backdrop-blur-md px-4 py-1.5 text-xs font-semibold tracking-wide text-brand mb-8 shadow-sm"
            data-testid="hero-badge"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse-soft" />
            {t("hero_badge")}
          </motion.div>

          <h1 className="font-heading font-bold text-ink tracking-tighter leading-[0.98] text-4xl sm:text-6xl lg:text-[5.2rem] text-balance">
            <MaskedLine delay={0.35}>{t("hero_line1")}</MaskedLine>
            <MaskedLine delay={0.5}>{t("hero_line2")}</MaskedLine>
            <MaskedLine delay={0.65}>
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-brand to-aqua">{t("hero_line3")}</span>
            </MaskedLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.8 }}
            className="mt-7 max-w-xl text-base sm:text-lg text-ink/60 leading-relaxed"
            data-testid="hero-subtitle"
          >
            {t("hero_sub")}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.1, duration: 0.8 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <button
              data-testid="hero-cta-evaluation"
              onClick={() => scrollTo("iletisim")}
              className="group px-7 py-3.5 rounded-full bg-brand text-white font-heading font-semibold text-sm flex items-center gap-2 hover:bg-branddeep shadow-lg shadow-brand/25 transition-colors duration-300"
            >
              {t("hero_cta1")}
              <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform duration-300" />
            </button>
            <button
              data-testid="hero-cta-treatments"
              onClick={() => scrollTo("tedaviler")}
              className="px-7 py-3.5 rounded-full bg-white border border-black/10 text-ink font-heading font-semibold text-sm hover:border-brand/40 hover:text-brand transition-colors duration-300"
            >
              {t("hero_cta2")}
            </button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.4, duration: 1 }}
            className="mt-14 flex flex-wrap gap-10 border-t border-black/10 pt-8"
          >
            {[
              { v: String(TREATMENTS.length), k: "stat_treatments" },
              { v: String(HOSPITALS.length), k: "stat_hospitals" },
              { v: "24/7", k: "stat_support" },
            ].map((s) => (
              <div key={s.k}>
                <div className="font-heading text-3xl font-bold text-ink">{s.v}</div>
                <div className="text-xs text-sub tracking-wide uppercase mt-1">{t(s.k)}</div>
              </div>
            ))}
          </motion.div>
        </motion.div>

        <motion.div style={{ y: baymaxY }} className="relative hidden lg:flex justify-center items-center" data-testid="hero-baymax">
          <div className="absolute w-[420px] h-[420px] rounded-full bg-white/70 border border-aqua/40" />
          <div className="absolute w-[300px] h-[300px] rounded-full bg-aqua/20" />
          <motion.img
            src={IMAGES.mascot}
            alt="Tudi"
            data-testid="hero-baymax-img"
            onClick={openChat}
            initial={{ opacity: 0, scale: 0.85 }}
            animate={{ opacity: 1, scale: 1, y: [0, -16, 0] }}
            transition={{
              opacity: { delay: 0.6, duration: 0.8 },
              scale: { delay: 0.6, duration: 0.8 },
              y: { repeat: Infinity, duration: 4.5, ease: "easeInOut", delay: 1.4 },
            }}
            whileHover={{ scale: 1.06 }}
            whileTap={{ scale: 0.96 }}
            className="relative z-10 w-[340px] drop-shadow-2xl cursor-pointer"
          />
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.6, duration: 0.7 }}
            onClick={openChat}
            data-testid="hero-tudi-card"
            className="absolute -bottom-2 right-6 z-20 flex items-center gap-3 rounded-2xl bg-white/85 backdrop-blur-xl border border-black/5 shadow-xl px-4 py-3 cursor-pointer hover:shadow-2xl transition-shadow duration-300"
          >
            <span className="w-9 h-9 rounded-full bg-brand/10 text-brand flex items-center justify-center">
              <MessageCircleHeart size={17} />
            </span>
            <div className="leading-tight">
              <div className="text-sm font-heading font-bold text-ink">Tudi</div>
              <div className="text-[11px] text-sub">{t("chat_sub")}</div>
            </div>
          </motion.div>
        </motion.div>
      </div>

      <motion.div animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 2 }} className="absolute bottom-6 left-1/2 -translate-x-1/2 text-ink/30 z-10">
        <ChevronDown size={22} />
      </motion.div>
    </section>
  );
}
